import { TenantProcess } from './tenantProcessStateBase';

export enum CopilotPanelContext {
  Generic = 1,
  AnnualPlan = 2,
  Library = 3,
  TrialWelcome = 4,
  Processes = 5,
  ControlEvidenceAudit = 6,
  ControlDocumentationAudit = 7,
  TaskEvidenceAudit = 8,
  NotAvailable = 999,
}

export type CopilotBridgeState = {
  isOpen: boolean;
  isPinned: boolean;
  activeContext: CopilotPanelContext;
  isLoading: boolean;
};

export type CopilotContextOwner = symbol;

type CopilotContextRegistration = {
  context: CopilotPanelContext;
  content: CopilotPanelContentBase;
};

const unsupportedContextCleanupDelayMs = 250;

export class CopilotBridge {
  // Initialize with state change callback to notify when bridge state changes (e.g. panel open/close, context change) for rerendering.
  //
  // Business rules and user flows:
  // - Scenes that support Copilot must register through `useCopilotContext`, which calls `activateContext`.
  // - Normal supported scenes only publish context; they only open the panel automatically when it is pinned and the
  //   user has not explicitly closed the panel.
  // - Explicit flows can still force the panel open/pinned through the hook options, for example trial welcome.
  // - When navigating between two supported scenes, the panel stays open and receives the new scene content.
  // - When the last supported scene releases its context, unsupported cleanup is deferred briefly so a new scene can claim
  //   Copilot during the same React route transition. If no scene claims it, the bridge closes the panel even when pinned.
  // - Pinning remains a user preference for supported scenes; navigating to an unsupported scene closes the panel but does
  //   not clear the stored pin preference.
  // - Closing because a scene is unsupported is transient. It must not mark the panel as user-closed, so a pinned panel can
  //   open again when the user returns to a supported scene.
  // - Closing from the panel close button, panel dismiss, navbar toggle, or hotkey is user intent. The UI persists that
  //   intent and restores it with `restoreUserClosedState`, so pinned auto-open stays disabled until the navbar/hotkey opens
  //   the panel again or a flow uses forceOpen.
  // - The panel component owns rendering: supported contexts render their content, unsupported contexts render the
  //   not-available state, and disabled AI renders the activation state.
  constructor(
    private readonly onStateChange?: (state: CopilotBridgeState, changed: Partial<CopilotBridgeState>) => void,
  ) {}

  private readonly contextRegistrations = new Map<CopilotContextOwner, CopilotContextRegistration>();

  private activeContextOwner: CopilotContextOwner | undefined = undefined;

  private pendingContextRelease: ReturnType<typeof setTimeout> | undefined = undefined;

  private setBridgeState(changed: Partial<CopilotBridgeState>): void {
    this.onStateChange?.(
      {
        isOpen: this._isOpen,
        isPinned: this.isPinned,
        activeContext: this._activeContext,
        isLoading: this._isLoading,
      },
      changed,
    );
  }

  //active context
  private _activeContext: CopilotPanelContext = CopilotPanelContext.NotAvailable;

  get activeContext(): CopilotPanelContext {
    return this._activeContext;
  }

  private panelContextHandler: ((context: CopilotPanelContext, content: CopilotPanelContentBase) => void) | null = null;

  private startProcessHandler: ((processId: TenantProcess) => void | Promise<void>) | null = null;

  public registerPanel(
    handler: (context: CopilotPanelContext, content: CopilotPanelContentBase) => void,
  ): CopilotSubscription {
    this.panelContextHandler = handler;
    const activeRegistration = this.getActiveContextRegistration();
    if (activeRegistration) {
      handler(activeRegistration.context, activeRegistration.content);
    }

    return new CopilotSubscription(() => {
      if (this.panelContextHandler === handler) this.panelContextHandler = null;
    });
  }

  public registerStartProcessHandler(handler: (processId: TenantProcess) => void | Promise<void>): CopilotSubscription {
    this.startProcessHandler = handler;

    return new CopilotSubscription(() => {
      if (this.startProcessHandler === handler) this.startProcessHandler = null;
    });
  }

  public requestStartProcess(processId: TenantProcess): void {
    this.openFromUser();
    void this.startProcessHandler?.(processId);
  }

  // Registers or refreshes Copilot support for one mounted scene instance.
  // Scene components should normally use `useCopilotContext` instead of calling this directly.
  public activateContext(
    owner: CopilotContextOwner,
    context: CopilotPanelContext,
    content: CopilotPanelContentBase,
  ): void {
    const isActivatingFromUnsupportedScene =
      this.contextRegistrations.size === 0 && this.activeContextOwner === undefined;
    this.cancelPendingContextRelease();
    this.contextRegistrations.delete(owner);
    this.contextRegistrations.set(owner, { context, content });
    this.activeContextOwner = owner;
    this.publishContext(context, content);
    if (isActivatingFromUnsupportedScene && this.isPinned && !this.isPinnedAutoOpenDismissed) {
      this.isOpen = true;
    }
  }

  // Releases Copilot support for one mounted scene instance. If another supported scene is still registered,
  // that scene becomes active; otherwise the panel is closed after the current route transition has a chance
  // to register a replacement context.
  public releaseContext(owner: CopilotContextOwner): void {
    if (!this.contextRegistrations.has(owner)) return;

    const wasActiveContext = this.activeContextOwner === owner;
    this.contextRegistrations.delete(owner);
    if (!wasActiveContext) {
      if (this.contextRegistrations.size === 0) {
        this.activeContextOwner = undefined;
        this.scheduleUnsupportedContextCleanup();
      }

      return;
    }

    const fallbackContext = this.getLastContextRegistration();
    if (fallbackContext) {
      this.activeContextOwner = fallbackContext.owner;
      this.publishContext(fallbackContext.registration.context, fallbackContext.registration.content);

      return;
    }

    this.activeContextOwner = undefined;
    this.scheduleUnsupportedContextCleanup();
  }

  private publishContext(context: CopilotPanelContext, content: CopilotPanelContentBase): void {
    const previousContext = this._activeContext;
    this._activeContext = context;
    if (previousContext !== context) {
      this.setBridgeState({ activeContext: context });
    }
    this.panelContextHandler?.(context, content);
  }

  // Clears all scene registrations and closes the panel. Use this for global invalidation, such as signing out;
  // scene navigation should go through `releaseContext` so supported-to-supported handoffs do not flash.
  public clearContext(): void {
    this.deactivateContext();
  }

  // Route changes can land on unsupported scenes that do not register a replacement context.
  // Defer cleanup so supported route-to-route transitions still have a chance to claim the bridge.
  public closeIfUnsupported(): void {
    this.scheduleUnsupportedContextCleanup();
  }

  // Notifies Copilot consumers that external state affecting the panel changed, without making the bridge own that state.
  public notifyStateChanged(): void {
    this.setBridgeState({});
  }

  private getLastContextRegistration():
    | { owner: CopilotContextOwner; registration: CopilotContextRegistration }
    | undefined {
    let latestContext: { owner: CopilotContextOwner; registration: CopilotContextRegistration } | undefined = undefined;
    this.contextRegistrations.forEach((registration, owner) => {
      latestContext = { owner, registration };
    });

    return latestContext;
  }

  private getActiveContextRegistration(): CopilotContextRegistration | undefined {
    if (this.activeContextOwner === undefined) return undefined;

    return this.contextRegistrations.get(this.activeContextOwner);
  }

  private scheduleUnsupportedContextCleanup(): void {
    this.cancelPendingContextRelease();
    this.pendingContextRelease = setTimeout(() => {
      this.pendingContextRelease = undefined;
      if (this.getActiveContextRegistration() || this.contextRegistrations.size > 0) return;
      this.deactivateContext();
    }, unsupportedContextCleanupDelayMs);
  }

  private cancelPendingContextRelease(): void {
    if (this.pendingContextRelease === undefined) return;
    clearTimeout(this.pendingContextRelease);
    this.pendingContextRelease = undefined;
  }

  private deactivateContext(): void {
    this.cancelPendingContextRelease();
    this.contextRegistrations.clear();
    this.activeContextOwner = undefined;
    this.isOpen = false;
    this.isLoading = false;
    this.publishContext(CopilotPanelContext.NotAvailable, new CopilotEmptyContent());
  }

  // Properties that can trigger panel state change for rerendering, e.g. open/close, pin/unpin, loading state, etc.
  private _isOpen: boolean = false;

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    if (this._isOpen === value) return;
    if (value) {
      this.isPinnedAutoOpenDismissed = false;
    }
    this._isOpen = value;
    this.setBridgeState({ isOpen: value });
  }

  public restoreUserClosedState(value: boolean): void {
    this.isPinnedAutoOpenDismissed = value;
    if (value) {
      this.isOpen = false;
    }
  }

  public openFromUser(): void {
    this.isPinnedAutoOpenDismissed = false;
    this.isOpen = true;
  }

  public closeFromUser(): void {
    this.isPinnedAutoOpenDismissed = true;
    this.isOpen = false;
  }

  private _isPinned: boolean = false;

  private _forcedPinned: boolean | undefined = undefined;

  get isPinned() {
    return this._forcedPinned ?? this._isPinned;
  }

  set isPinned(value: boolean) {
    if (this._isPinned === value) return;
    this.isPinnedAutoOpenDismissed = false;
    this._isPinned = value;
    this.setBridgeState({ isPinned: this.isPinned });
  }

  private isPinnedAutoOpenDismissed: boolean = false;

  get isPinnedForced() {
    return this._forcedPinned !== undefined;
  }

  public forcePinned(value: boolean | undefined): void {
    if (this._forcedPinned === value) return;
    if (this._forcedPinned === true && value === undefined && this._isOpen) {
      this._isPinned = true;
    }
    this._forcedPinned = value;
    this.setBridgeState({ isPinned: this.isPinned });
  }

  private _isLoading: boolean = false;

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    if (this._isLoading === value) return;
    this._isLoading = value;
    this.setBridgeState({ isLoading: value });
  }
}

export class CopilotSubscription {
  private isDisposed = false;

  constructor(private readonly disposeFn: () => void) {}

  public dispose(): void {
    if (this.isDisposed) return;
    this.isDisposed = true;
    this.disposeFn();
  }
}

export abstract class CopilotPanelContentBase {
  public abstract readonly context: CopilotPanelContext;
}

export class CopilotEmptyContent extends CopilotPanelContentBase {
  public readonly context = CopilotPanelContext.NotAvailable;
}
